import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import { useCallback, useEffect, useMemo, useState } from "react";
import { buscarRelatorios } from "../../services/api-relatorios.service";
import { Dialog } from "@mui/material";
import BotaoRelatorio from "../../components/BotaoRelatorio";
import TableCardv2 from "../../components/TableCardv2";
import DescriptionIcon from "@mui/icons-material/Description";
import NovoExportador from "../../components/BotaoRelatorio/NovoExportador";
import FiltroSalvos from "./FiltroSalvos";
import ModalExtracaoIcmsSt from "../../components/ExtracaoIcmsSt/ModalExtracaoIcmsSt";
import ModalExtracaoConciliacaoSped from "../../components/ExtracaoConciliacaoSped/ModalExtracaoConciliacaoSped";
import SnackBar from "../../components/SnackBar";
import { isRelatorioIcmsSt } from "../../utils/relatorioIcmsSt.util";
import { isRelatorioConciliacaoSped } from "../../utils/relatorioConciliacaoSped.util";
import styles from "./styles.module.css";

const header = {
  icon: <FormatListBulletedIcon fontSize="1rem" />,
  title: "Extrator de Relatórios",
};

const columns = [
  { key: "nome", label: "Nome" },
  { key: "tipo", label: "Tipo" },
];

function montarLinhas(dados, handlers) {
  const { onExtrair, onFiltros } = handlers;
  return (dados || []).map((item) => ({
    nome: item.nome,
    tipo: item.tipoRelatorio?.descricao ?? "",
    btn: [
      {
        icon: <DescriptionIcon />,
        title: "Extrair",
        onClick: () => onExtrair(item),
        variant: "primary",
      },
      {
        icon: <FilterAltIcon />,
        title: "Filtros",
        onClick: () => onFiltros(item.codigoRelatorio, item.idRelatorio),
        variant: "primary",
      },
    ],
  }));
}

const ExtratorRelatorios = () => {
  const [baseRows, setBaseRows] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingTable, setLoadingTable] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [codigoRelatorio, setCodigoRelatorio] = useState("");
  const [idRelatorio, setIdRelatorio] = useState("");
  const [openExtrair, setOpenExtrair] = useState(false);
  const [openFiltros, setOpenFiltros] = useState(false);
  const [openModalIcmsSt, setOpenModalIcmsSt] = useState(false);
  const [relatorioIcmsSt, setRelatorioIcmsSt] = useState(null);
  const [openModalConciliacaoSped, setOpenModalConciliacaoSped] =
    useState(false);
  const [relatorioConciliacaoSped, setRelatorioConciliacaoSped] =
    useState(null);
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });

  const handleOpenExtrair = useCallback((item) => {
    if (isRelatorioConciliacaoSped(item.nome, item.codigoRelatorio)) {
      setRelatorioConciliacaoSped({
        idRelatorio: item.idRelatorio,
        codigoRelatorio: item.codigoRelatorio,
        nome: item.nome,
      });
      setOpenModalConciliacaoSped(true);
      return;
    }
    if (isRelatorioIcmsSt(item.nome, item.codigoRelatorio)) {
      setRelatorioIcmsSt({
        idRelatorio: item.idRelatorio,
        codigoRelatorio: item.codigoRelatorio,
        nome: item.nome,
      });
      setOpenModalIcmsSt(true);
      return;
    }
    setOpenExtrair(true);
    setCodigoRelatorio(item.codigoRelatorio);
  }, []);

  const handleCloseExtrair = () => {
    setOpenExtrair(false);
  };

  const handleCloseModalIcmsSt = () => {
    setOpenModalIcmsSt(false);
    setRelatorioIcmsSt(null);
  };

  const handleCloseModalConciliacaoSped = () => {
    setOpenModalConciliacaoSped(false);
    setRelatorioConciliacaoSped(null);
  };

  const handleOpenFiltros = useCallback((cod, id) => {
    setIdRelatorio(id);
    setCodigoRelatorio(cod);
    setOpenFiltros(true);
  }, []);

  const handleCloseFiltros = () => {
    setOpenFiltros(false);
  };

  const handlers = useMemo(
    () => ({
      onExtrair: handleOpenExtrair,
      onFiltros: handleOpenFiltros,
    }),
    [handleOpenExtrair, handleOpenFiltros]
  );

  const fetchLista = useCallback(
    async ({ page: p, limit: l, showFullPageLoading }) => {
      if (showFullPageLoading) setLoading(true);
      else setLoadingTable(true);
      try {
        const response = await buscarRelatorios({
          apuracao: false,
          page: p,
          limit: l,
        });
        setTotal(Number(response.total) || 0);
        setPage(p);
        setLimit(l);
        setBaseRows(montarLinhas(response.relatorios ?? [], handlers));
      } catch (error) {
        setBaseRows([]);
        setTotal(0);
        setObjSnackBar({
          type: "error",
          msg:
            error.response?.data?.mensagem ||
            error.response?.data?.message ||
            "Erro ao carregar relatórios.",
        });
      } finally {
        if (showFullPageLoading) setLoading(false);
        else setLoadingTable(false);
      }
    },
    [handlers]
  );

  useEffect(() => {
    fetchLista({
      page: 1,
      limit: 10,
      showFullPageLoading: true,
    });
  }, [fetchLista]);

  const handlePageChange = useCallback(
    (newPage) => {
      fetchLista({
        page: newPage,
        limit,
        showFullPageLoading: false,
      });
    },
    [fetchLista, limit]
  );

  const handleRowsPerPageChange = useCallback(
    (newLimit) => {
      fetchLista({
        page: 1,
        limit: newLimit,
        showFullPageLoading: false,
      });
    },
    [fetchLista]
  );

  const paginationConfig = useMemo(
    () => ({
      serverSideTotal: total,
      serverSidePage: page,
      serverSideRowsPerPage: limit,
      onServerPageChange: handlePageChange,
      onServerRowsPerPageChange: handleRowsPerPageChange,
      disabled: loadingTable,
    }),
    [
      total,
      page,
      limit,
      handlePageChange,
      handleRowsPerPageChange,
      loadingTable,
    ]
  );

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  return (
    <>
      <div className={styles.container}>
        <TableCardv2
          header={header}
          columns={columns}
          data={baseRows}
          loading={loading}
          loadingTable={loadingTable}
          paginationConfig={paginationConfig}
        />
      </div>

      <Dialog open={openModal} onClose={handleCloseModal}>
        <BotaoRelatorio codigoRelatorio={codigoRelatorio} />
      </Dialog>
      <NovoExportador
        handleModal={openExtrair}
        codRelatorio={codigoRelatorio}
        close={handleCloseExtrair}
      />
      <FiltroSalvos
        open={openFiltros}
        idRelatorio={idRelatorio}
        codRelatorio={codigoRelatorio}
        close={handleCloseFiltros}
      />
      {relatorioIcmsSt && (
        <ModalExtracaoIcmsSt
          open={openModalIcmsSt}
          onClose={handleCloseModalIcmsSt}
          idRelatorio={relatorioIcmsSt.idRelatorio}
          codigoRelatorio={relatorioIcmsSt.codigoRelatorio}
          nomeRelatorio={relatorioIcmsSt.nome}
          onSucesso={(msg) => {
            setObjSnackBar({
              type: "success",
              msg: msg || "Relatório ICMS-ST gerado com sucesso.",
            });
          }}
        />
      )}
      {relatorioConciliacaoSped && (
        <ModalExtracaoConciliacaoSped
          open={openModalConciliacaoSped}
          onClose={handleCloseModalConciliacaoSped}
          idRelatorio={relatorioConciliacaoSped.idRelatorio}
          codigoRelatorio={relatorioConciliacaoSped.codigoRelatorio}
          nomeRelatorio={relatorioConciliacaoSped.nome}
          onSucesso={(msg) => {
            setObjSnackBar({
              type: "success",
              msg: msg || "Relatório em processamento!",
            });
          }}
        />
      )}
      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={setObjSnackBar} />
    </>
  );
};

export default ExtratorRelatorios;
